import { useMemo, useEffect, useState, Fragment } from 'react';
import { isEmpty } from 'lodash';
import { Flex, Grid } from 'antd';
import useForm from 'hooks/useForm';
import { useTranslation, EI18nLanguageUI } from '@packages/utils';
import { Row, Col } from 'components/Base/Grid';
import TrashIcon from 'assets/images/trash.svg';
import useGetValidationData from 'components/Base/Form/validationRules';
import { Form, FormItem } from 'components/Base/Form';
import { Image } from 'components/Base/Image';
import { FormInstance } from 'components/Base/Form/Form.types';
import { Input, PhoneInput } from 'components/Base/Input';
import { Paragraph, Title } from 'components/Base/Typography';
import { NextStepButton } from 'components/Shared/RegisterAction/NewRegistrationModal/components/RegistrationContent/RegistationContent.style';
import {
  chooseLanguageSecionKey,
  inputPlaceholdersKey,
  stepThreeKey,
  inputLabelsKey,
} from 'components/Shared/RegisterAction/RegistrationModal/RegistrationModal.data';
import styled from 'styled-components';
import { MobileDivider } from '../StepWrapper.style';
import useRegistrationContext, {
  LANGUAGE_TRANSLATE,
  NO_CHILD_INFORMATION_REQUIRED,
} from '../../context/NewRegistrationModalContext';
import { ELanguage, TOption } from '../../../../../../data/types/generic.types';
import ProgressStepper from '../../components/ProgressStepper';
import BackNextButtons from '../../components/BackNextButtons';
import {
  convertAdultsToObject,
  JoinWithAnd,
  parseAdultDetails,
} from '../../utils/utils';
import { AdultWarning, CapacityWarning } from '../../components/Warnings';
import { AddChildButton } from '../ChildInformation';

const StyledInput = styled(Input)`
  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
`;

const AdultRow = styled(Row)`
  @media (max-width: 768px) {
    row-gap: 8px;
  }
`;

const AdultInputWrapper = styled.div`
  margin-bottom: 24px;
  @media (max-width: 768px) {
    row-gap: 8px;
  }
`;

type AdultDetailsType = {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  ageGroup: string;
  additionalAdults: {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
  }[];
};

type ChildrenGroups = {
  [key: string]: {
    ageGroup: string;
    name: string;
  }[];
};

const AdultInfoForm = ({
  isProvider,
  mainIndex,
  handleInputChange,
  isAdditional,
  additionalIndex,
}: {
  isProvider?: boolean;
  mainIndex: number;
  handleInputChange: (
    event: {
      target: {
        value: string;
      };
    },
    key: string,
  ) => void;
  isAdditional: boolean;
  additionalIndex?: number;
}) => {
  const { validationRules } = useGetValidationData({
    lng: 'translate',
  });
  const { t: tStepThree } = useTranslation({
    keyPrefix: stepThreeKey,
    lng: 'translate',
  });
  const { t: tInput } = useTranslation({
    keyPrefix: inputPlaceholdersKey,
    lng: 'translate',
  });
  const { t: tLabels } = useTranslation({
    keyPrefix: inputLabelsKey,
    lng: 'translate',
  });

  type AdultFormItemConfig = {
    label: string;
    providerLabel?: string;
    name: string;
    rules?: any[];
    placeholder: string;
    hasProviderLabel?: boolean;
    onChange: (event: any) => void;
    styles?: React.CSSProperties;
    dataCy?: string;
  };

  const adultFormMapper: AdultFormItemConfig[] = [
    {
      label: tStepThree('ADULT_FIRSTNAME'),
      providerLabel: tLabels('PROVIDER_FIRST_NAME'),
      name: `${isAdditional ? 'additional-' : ''}adult-firstName-${mainIndex}${
        isAdditional ? `-${additionalIndex}` : ''
      }`,
      rules: [validationRules.requiredField()],
      placeholder: tInput('ENTER_FIRST_NAME'),
      hasProviderLabel: isProvider,
      dataCy: 'input_adult_first_name',
      onChange: (event: any) =>
        handleInputChange(
          event,
          isAdditional ? 'additionalFirstName' : 'firstName',
        ),
    },
    {
      label: tStepThree('ADULT_LASTNAME'),
      providerLabel: tLabels('PROVIDER_LAST_NAME'),
      name: `${isAdditional ? 'additional-' : ''}adult-lastName-${mainIndex}${
        isAdditional ? `-${additionalIndex}` : ''
      }`,
      rules: [validationRules.requiredField()],
      placeholder: tInput('ENTER_LAST_NAME'),
      hasProviderLabel: isProvider,
      dataCy: 'input_adult_last_name',
      onChange: (event: any) =>
        handleInputChange(
          event,
          isAdditional ? 'additionalLastName' : 'lastName',
        ),
    },
    {
      label: tStepThree('EMAIL'),
      name: `${isAdditional ? 'additional-' : ''}adult-email-${mainIndex}${
        isAdditional ? `-${additionalIndex}` : ''
      }`,
      rules: [validationRules.requiredField(), validationRules.email()],
      placeholder: tInput('ENTER_EMAIL'),
      hasProviderLabel: false,
      dataCy: 'input_email',
      onChange: (event: any) =>
        handleInputChange(event, isAdditional ? 'additionalEmail' : 'email'),
    },
    {
      label: tStepThree('MOBILE'),
      name: `${isAdditional ? 'additional-' : ''}adult-mobile-${mainIndex}${
        isAdditional ? `-${additionalIndex}` : ''
      }`,
      rules: [validationRules.requiredField(), validationRules.phoneNumber()],
      placeholder: tInput('ENTER_MOBILE'),
      hasProviderLabel: false,
      dataCy: 'input_mobile_num',
      onChange: (event: any) =>
        handleInputChange(event, isAdditional ? 'additionalMobile' : 'mobile'),
    },
    {
      label: 'Age Group',
      name: `${isAdditional ? 'additional-' : ''}adult-ageGroup-${mainIndex}${
        isAdditional ? `-${additionalIndex}` : ''
      }`,
      placeholder: '',
      hasProviderLabel: false,
      onChange: () => {
        // do nothing
      },
      styles: {
        visibility: 'hidden',
        position: 'absolute',
      } as React.CSSProperties,
    },
  ];

  return (
    <AdultRow
      gutter={[
        { xs: 0, md: 16 },
        { xs: 0, md: 0 },
      ]}
    >
      {adultFormMapper.map(formItem => {
        const isMobileField = formItem.name.includes('mobile');
        return (
          <Col
            xs={24}
            md={12}
            key={`${formItem.name}-wrapper`}
            style={{ ...formItem?.styles }}
          >
            <FormItem
              label={
                formItem.hasProviderLabel
                  ? formItem.providerLabel
                  : formItem.label
              }
              name={formItem.name}
              rules={formItem.rules}
            >
              {isMobileField ? (
                <PhoneInput
                  placeholder={formItem.placeholder}
                  onChange={formItem.onChange}
                  data-cy={formItem.dataCy}
                />
              ) : (
                <StyledInput
                  placeholder={formItem.placeholder}
                  onChange={formItem.onChange}
                  data-cy={formItem.dataCy}
                />
              )}
            </FormItem>
          </Col>
        );
      })}
    </AdultRow>
  );
};

const AdultInput = ({
  form,
  index,
  adultDetails,
  adultValues,
  setAdultValues,
  childrenGroups,
  isProvider = false,
}: {
  form: FormInstance;
  index: number;
  adultDetails: AdultDetailsType;
  adultValues: AdultDetailsType[];
  setAdultValues: React.Dispatch<React.SetStateAction<AdultDetailsType[]>>;
  childrenGroups: ChildrenGroups;
  isProvider?: boolean;
}) => {
  const { t: tStepThree } = useTranslation({
    keyPrefix: stepThreeKey,
    lng: 'translate',
  });

  const handleInputChange = (
    event: { target: { value: string } },
    key: string,
  ) => {
    const details = { ...adultDetails, [key]: event.target.value };
    form.setFieldsValue({ [key]: event.target.value });
    const updatedValues = adultValues;
    updatedValues[index] = details;
    setAdultValues([...updatedValues]);
  };

  // Commented out - Add Adult functionality disabled
  // const isAddEnabled = useMemo(() => {
  //   const { firstName, lastName, mobile, email } = adultDetails;
  //
  //   return [firstName, lastName, mobile, email].every(
  //     value => value && value !== '',
  //   );
  // }, [adultDetails]);

  // const onAddAdult = (e: any) => {
  //   e.preventDefault();
  //   const initialValue = {
  //     firstName: '',
  //     lastName: '',
  //     email: '',
  //     mobile: '',
  //   };
  //   const updatedAdultValues = adultValues;
  //   const updatedAdditionalAdults = updatedAdultValues[index].additionalAdults;
  //   if (updatedAdditionalAdults && isAddEnabled) {
  //     updatedAdditionalAdults.push(initialValue);
  //     updatedAdultValues[index] = {
  //       ...updatedAdultValues[index],
  //       additionalAdults: [...updatedAdditionalAdults],
  //     };
  //     setAdultValues([...updatedAdultValues]);
  //   }
  // };

  const onRemoveAdult = (e: any, additionalIndex: number) => {
    e.preventDefault();
    const updatedAdultValues = adultValues;
    const updatedAdditionalAdults = updatedAdultValues[index].additionalAdults;
    updatedAdditionalAdults.splice(additionalIndex, 1);
    updatedAdultValues[index] = {
      ...updatedAdultValues[index],
      additionalAdults: [...updatedAdditionalAdults],
    };
    setAdultValues([...updatedAdultValues]);
  };

  const childrenNames =
    childrenGroups[adultDetails.ageGroup]?.map(
      (child: { ageGroup: string; name: string }) => child.name,
    ) || [];

  const descriptionText = childrenNames.length
    ? tStepThree('DESCRIPTION')
    : tStepThree('DESCRIPTION').replace(/ for\s*$/, '');

  const screens = Grid.useBreakpoint();
  const paragraphClass = !screens.md ? 'mb-4' : '';

  return (
    <AdultInputWrapper key={`adult-form-${index}`}>
      <Paragraph className="mb-2" data-cy="registration-children-names">
        {descriptionText}
        {childrenNames.length > 0 && (
          <strong>{JoinWithAnd(childrenNames)}</strong>
        )}
        .
      </Paragraph>
      <AdultInfoForm
        isProvider={isProvider}
        mainIndex={index}
        handleInputChange={handleInputChange}
        isAdditional={false}
        data-cy="registration-adult-information"
      />
      {/* Hidden: Add Adult button
      {!adultDetails.additionalAdults?.length && (
        <AddChildButton
          $disabled={!isAddEnabled}
          onClick={(e: any) => onAddAdult(e)}
          data-cy="btn_add_adult"
        >
          + {tStepThree('ADD_ADULT')}
        </AddChildButton>
      )}
      */}
      {adultDetails.additionalAdults.map((additionalAdult, additionalIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`additional-adult-wrapper-${index}-${additionalIndex}`}>
          <hr style={{ borderTop: '1px solid #DEDEDE' }} />
          <Flex justify="space-between" align="center">
            <Paragraph
              fontWeight={400}
              fontFamily="Lato"
              className={paragraphClass}
              style={{ letterSpacing: 0 }}
            >
              {tStepThree('ADDITIONAL_ADULT')}
            </Paragraph>
            <AddChildButton
              onClick={(e: any) => onRemoveAdult(e, additionalIndex)}
            >
              <Image alt="Registration Modal Art" src={TrashIcon} />
            </AddChildButton>
          </Flex>
          <AdultInfoForm
            isProvider={isProvider}
            mainIndex={index}
            additionalIndex={additionalIndex}
            handleInputChange={handleInputChange}
            isAdditional
          />
        </Fragment>
      ))}
    </AdultInputWrapper>
  );
};

const AdultInformation = () => {
  const [adultValues, setAdultValues] = useState<AdultDetailsType[]>([]);
  const [childrenGroups, setChildrenGroups] = useState<ChildrenGroups>({});
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const {
    eventDetails,
    registrationValues,
    adultsInformation,
    setAdultsInformation,
    childrenInformation,
    stepper,
    setStepper,
    englishGroupCapacities,
    spanishGroupCapacities,
  } = useRegistrationContext();
  const { t: tEnum } = useTranslation({ lng: 'translate' });
  const { t: tButton } = useTranslation({
    keyPrefix: chooseLanguageSecionKey,
    lng: 'translate',
  });
  const { form } = useForm();
  const lang = registrationValues.userLanguage;
  const selectedLanguage = LANGUAGE_TRANSLATE[lang as EI18nLanguageUI];
  const { t: tStepThree } = useTranslation({
    keyPrefix: stepThreeKey,
    lng: 'translate',
  });

  const { ageGroupOptions } = eventDetails;

  const ageGroupOptionsArr = useMemo(() => {
    if (!selectedLanguage) return [];
    const options: TOption[] = [];
    const language = selectedLanguage as ELanguage;
    if (ageGroupOptions) {
      options.push(...(ageGroupOptions[language] || []));
    }
    return options.map(age => ({
      ...age,
      label: tEnum(`enums.age-group.${age.label}`),
    }));
  }, [selectedLanguage, ageGroupOptions]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const arrayValues = parseAdultDetails(values);
      setAdultsInformation(arrayValues);
      setStepper(stepper + 1);
    } catch (e) {
      // do nothing
    }
  };

  const onBack = async () => {
    try {
      const values = await form.getFieldsValue();
      const arrayValues = parseAdultDetails(values);
      setAdultsInformation(arrayValues);

      setStepper(stepper - 1);
    } catch (e) {
      // do nothing
    }
  };

  // Initialize at least one adult form even if no age groups
  useEffect(() => {
    const baseAdults = ageGroupOptionsArr.length
      ? ageGroupOptionsArr.map(group => ({
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          ageGroup: group.value,
          additionalAdults: [],
        }))
      : [
          {
            firstName: '',
            lastName: '',
            email: '',
            mobile: '',
            ageGroup: 'DEFAULT',
            additionalAdults: [],
          },
        ];

    const initialValue = [...baseAdults];
    if (!isEmpty(adultsInformation)) {
      adultsInformation.forEach((adult, index) => {
        initialValue[index] = { ...initialValue[index], ...adult };
      });
    }
    setAdultValues([...initialValue]);
    const adultFieldInformation = convertAdultsToObject(initialValue);
    form.setFieldsValue(adultFieldInformation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (childrenInformation?.length) {
      const grouped = childrenInformation.reduce(
        (acc: ChildrenGroups, item) => {
          const { ageGroup } = item;
          const checkedAgeGroup = ageGroup || ageGroupOptionsArr[0].value;
          if (!acc[checkedAgeGroup]) {
            acc[checkedAgeGroup] = [];
          }
          acc[checkedAgeGroup].push(item);
          return acc;
        },
        {},
      );
      setChildrenGroups(grouped);
    }
  }, [childrenInformation]);

  // Set warning if the only age group is full
  useEffect(() => {
    if (
      selectedLanguage === ELanguage.SPANISH &&
      spanishGroupCapacities &&
      Object.keys(spanishGroupCapacities)?.length === 1
    ) {
      Object.keys(spanishGroupCapacities).forEach(key => {
        if (spanishGroupCapacities[key].full) {
          setShowWarning(true);
        }
      });
    } else if (
      selectedLanguage === ELanguage.ENGLISH &&
      englishGroupCapacities &&
      Object.keys(englishGroupCapacities)?.length === 1
    ) {
      Object.keys(englishGroupCapacities).forEach(key => {
        if (englishGroupCapacities[key].full) {
          setShowWarning(true);
        }
      });
    }
  }, [englishGroupCapacities, spanishGroupCapacities]);

  return (
    <Flex vertical className="h-100">
      <ProgressStepper />
      <MobileDivider />
      {showWarning && (
        <CapacityWarning
          onClose={() => setShowWarning(false)}
          data-cy="registration-capacity-warning"
        />
      )}
      {ageGroupOptionsArr.length > 1 && (
        <AdultWarning data-cy="registration-adult-warning" />
      )}
      <Title
        style={{ fontSize: '24px', marginTop: 24 }}
        data-cy="registration-step3-title"
      >
        {tStepThree('TITLE')}
      </Title>
      <Form
        form={form}
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        {(ageGroupOptionsArr.length
          ? ageGroupOptionsArr
          : [{ value: 'DEFAULT' } as any]
        )?.map((ageGroup, index) => {
          const adult = (adultValues || []).find(
            a => a?.ageGroup === ageGroup.value,
          ) || {
            firstName: '',
            lastName: '',
            email: '',
            mobile: '',
            ageGroup: ageGroup.value,
            additionalAdults: [],
          };

          if (
            childrenGroups[ageGroup.value] &&
            childrenGroups[ageGroup.value].length > 0
          ) {
            return (
              <AdultInput
                key={ageGroup.value}
                form={form}
                index={index}
                adultDetails={adult}
                adultValues={adultValues}
                setAdultValues={setAdultValues}
                childrenGroups={childrenGroups}
                data-cy="registration-adult-input"
              />
            );
          }

          // If no children info or no age groups, still render a generic adult form
          return (
            <AdultInput
              key={ageGroup.value || 'DEFAULT'}
              form={form}
              index={index}
              adultDetails={adult}
              adultValues={adultValues}
              setAdultValues={setAdultValues}
              childrenGroups={childrenGroups}
              isProvider={NO_CHILD_INFORMATION_REQUIRED.includes(
                eventDetails?.eventType,
              )}
              data-cy="registration-adult-input"
            />
          );
        })}

        <BackNextButtons customNextButton onBack={onBack}>
          <NextStepButton
            color="primary"
            type="primary"
            htmlType="submit"
            style={{ marginLeft: 'auto' }}
            onClick={handleSubmit}
          >
            {tButton('NEXT_STEP_BTN')}
          </NextStepButton>
        </BackNextButtons>
      </Form>
    </Flex>
  );
};
export default AdultInformation;
